// =========================
// POST COMPONENT
// =========================
// External Libraries
import { LitElement, html, css } from 'lit';

// App Modules
import Auth from '../Auth';
import App from '../App';
import Utils from '../Utils';
import { gotoRoute } from '../Router';

// This is the PopinPost class that handles the post component
// It includes methods for rendering the post, handling user interactions,
class PopinPost extends LitElement {
  static properties = {
    post: { type: Object },
    selectedImageIndex: { type: Number },
    slideDirection: { type: String },
    likedByUser: { type: Boolean },
    dislikedByUser: { type: Boolean },
    commentText: { type: String },
    commentDisplayCount: { type: Number },
  };

  constructor() {
    super();
    this.post = null;
    this.selectedImageIndex = null;
    this.slideDirection = 'none';
    this.likedByUser = false;
    this.dislikedByUser = false;
    this.commentText = '';
    this.commentDisplayCount = 3;
  }

  confirmationCallback = null;

  // LIFECYCLE METHODS

  // This method is called when the component is first updated
  async firstUpdated() {
    const userId = Auth.currentUser?.id;
    this.likedByUser = this.post.likes?.includes(userId);
    this.dislikedByUser = this.post.dislikes?.includes(userId);
    this.post = await this.enrichPost(this.post);
  }

  // UI HANDLERS

  // This method is called when the user clicks outside the dialog
  closeDialog() {
    this.selectedImageIndex = null;
  }

  // This method is called when the user clicks outside the dialog
  handleDialogClick(e) {
    const imageEl = this.shadowRoot.querySelector('.expanded-image');
    if (!imageEl?.contains(e.target)) {
      this.shadowRoot.querySelector('#imageDialog')?.hide();
    }
  }

  // This method is called when the user clicks on an image
  openImageDialog(index) {
    this.selectedImageIndex = index;
    this.slideDirection = 'none';
    this.requestUpdate();
    this.shadowRoot.querySelector('#imageDialog').show();
  }

  // This method is called when the user navigates to the next or previous image
  navigateImage(direction) {
    const images = this.post?.images || [];
    const newIndex = this.selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      this.slideDirection = direction > 0 ? 'right' : 'left';
      this.selectedImageIndex = newIndex;
    }
  }

  // This method is called when the user types in the comment input
  handleCommentInput(e) {
    this.commentText = e.target.value;
  }

  // This method is called when the user clicks the "View more comments" button
  showMoreComments() {
    this.commentDisplayCount += 10;
  }

  // This method is called when the user clicks the "Delete" button
  showConfirmationDialog(message, onConfirm) {
    const dialog = this.shadowRoot?.getElementById('confirmDialog');
    const messageEl = this.shadowRoot?.getElementById('confirmMessage');
    if (dialog && messageEl) {
      messageEl.textContent = message;
      this.confirmationCallback = onConfirm;
      dialog.show();
    } else {
      console.warn('Confirm dialog not found in shadow DOM');
    }
  }

  // This method is called when the user makes an error
  showErrorDialog(message) {
    const dialog = this.shadowRoot.getElementById('errorDialog');
    const msgEl = this.shadowRoot.getElementById('errorMessage');
    if (dialog && msgEl) {
      msgEl.textContent = message;
      dialog.show();
    }
  }

  // DATA FETCHING

  // This method fetches the user data and enriches the post with user details
  // It also fetches the supplier details if the user is a supplier
  async enrichPost(post) {
    const enrichedPost = { ...post };
    const user = await this.fetchUser(post.userId);

    if (user?.role === 'supplier') {
      try {
        const supplier = await Utils.getSupplierDetails(user._id);
        if (supplier?.businessName) {
          user.businessName = supplier.businessName;
        }
      } catch (err) {
        console.warn('Failed to fetch supplier data:', err);
      }
    }

    user.profileImage = Utils.getProfileImage(user);
    user.displayName = Utils.getDisplayName(user);

    enrichedPost.userId = user;
    enrichedPost.comments = await Promise.all(
      (post.comments || []).map((c) => this.enrichComment(c))
    );

    return enrichedPost;
  }

  // This method fetches the user data from the server
  async fetchUser(userId) {
    try {
      const res = await fetch(
        `${App.apiBase}/user/${typeof userId === 'object' ? userId._id : userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  }

  // This method enriches the comment with user details
  async enrichComment(comment) {
    const user = await this.fetchUser(comment.userId);

    if (user) {
      if (user.role === 'supplier') {
        try {
          const supplier = await Utils.getSupplierDetails(user._id);
          if (supplier?.businessName) {
            user.businessName = supplier.businessName;
          }
        } catch (err) {
          console.warn('Failed to fetch supplier data (comment):', err);
        }
      }

      user.profileImage = Utils.getProfileImage(user);
      user.displayName = Utils.getDisplayName(user);
    }

    return {
      ...comment,
      userId: user || {
        firstName: 'User',
        lastName: '',
        profileImage: `${App.apiBase}/static/default-profile.png`,
        displayName: 'User',
      },
      likes: comment.likes || [],
      dislikes: comment.dislikes || [],
    };
  }

  // POST ACTIONS

  // This method toggles the like state of the post
  async toggleLike() {
    const userId = Auth.currentUser?.id;
    this.likedByUser = !this.likedByUser;
    this.dislikedByUser = false;
    this.post.likes = this.likedByUser
      ? [...(this.post.likes || []), userId]
      : this.post.likes.filter((id) => id !== userId);
    this.post.dislikes = this.post.dislikes?.filter((id) => id !== userId);
    this.requestUpdate();
    try {
      await fetch(`${App.apiBase}/post/${this.post._id}/like`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  }

  // This method toggles the dislike state of the post
  async toggleDislike() {
    const userId = Auth.currentUser?.id;
    this.dislikedByUser = !this.dislikedByUser;
    this.likedByUser = false;
    this.post.dislikes = this.dislikedByUser
      ? [...(this.post.dislikes || []), userId]
      : this.post.dislikes.filter((id) => id !== userId);
    this.post.likes = this.post.likes?.filter((id) => id !== userId);
    this.requestUpdate();
    try {
      await fetch(`${App.apiBase}/post/${this.post._id}/dislike`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
    } catch (err) {
      console.error('Error toggling dislike:', err);
    }
  }

  // COMMENT ACTIONS

  // This method submits a new comment to the server
  async submitComment(e) {
    e.preventDefault();
    const commentText = this.commentText.trim();
    if (!commentText) return;

    const tempUser = Auth.currentUser || {
      firstName: 'User',
      lastName: '',
      profileImage: null,
      _id: 'temp-id',
    };

    this.post.comments = [
      { text: commentText, userId: tempUser },
      ...this.post.comments,
    ];
    this.commentText = '';
    this.requestUpdate();

    try {
      const res = await fetch(`${App.apiBase}/post/${this.post._id}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ text: commentText }),
      });
      if (res.ok) {
        const updatedPost = await res.json();
        this.post = await this.enrichPost(updatedPost);
      } else {
        console.warn('Comment not saved on server');
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
    }
  }

  // This method toggles the like or dislike state of a comment
  async toggleCommentReaction(commentId, type) {
    const userId = Auth.currentUser?.id;
    const comment = this.post.comments.find((c) => c._id === commentId);
    if (!comment) return;

    const isLiked = comment.likes?.includes(userId);
    const isDisliked = comment.dislikes?.includes(userId);

    if (type === 'like') {
      comment.likes = isLiked
        ? comment.likes.filter((id) => id !== userId)
        : [...(comment.likes || []), userId];
      if (!isLiked)
        comment.dislikes = comment.dislikes?.filter((id) => id !== userId);
    } else {
      comment.dislikes = isDisliked
        ? comment.dislikes.filter((id) => id !== userId)
        : [...(comment.dislikes || []), userId];
      if (!isDisliked)
        comment.likes = comment.likes?.filter((id) => id !== userId);
    }

    this.requestUpdate();
    try {
      await fetch(
        `${App.apiBase}/post/${this.post._id}/comments/${commentId}/${type}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
    } catch (err) {
      console.error(`Error toggling comment ${type}:`, err);
    }
  }

  // This method deletes a comment from the server
  deleteComment(commentId) {
    this.showConfirmationDialog(
      'Are you sure you want to delete this comment?',
      async () => {
        try {
          const res = await fetch(
            `${App.apiBase}/post/${this.post._id}/comments/${commentId}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              },
            }
          );
          if (res.ok) {
            this.post.comments = this.post.comments.filter(
              (c) => c._id !== commentId
            );
            this.requestUpdate();
          } else {
            const data = await res.json();
            this.showErrorDialog(data.message || 'Failed to delete comment.');
          }
        } catch (err) {
          console.error('Error deleting comment:', err);
          this.showErrorDialog('An error occurred while deleting the comment.');
        }
      }
    );
  }

  // POST DELETION

  // This method deletes the post from the server
  deletePost() {
    this.showConfirmationDialog(
      'Are you sure you want to delete this post?',
      async () => {
        try {
          const res = await fetch(`${App.apiBase}/post/${this.post._id}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          });
          if (res.ok) {
            this.remove();
          } else {
            const data = await res.json();
            this.showErrorDialog(data.message || 'Failed to delete post.');
          }
        } catch (err) {
          console.error('Error deleting post:', err);
          this.showErrorDialog('An error occurred while deleting the post.');
        }
      }
    );
  }

  render() {
    const { userId, text, createdAt } = this.post || {};
    const images = this.post?.images || [];

    const profileImage = Utils.getProfileImage(userId);
    const authorName = Utils.getDisplayName(userId);

    const fullDate = new Date(createdAt).toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const currentImage =
      images.length && this.selectedImageIndex !== null
        ? `${App.apiBase}/uploads/${images[this.selectedImageIndex]}`
        : '';

    const visibleComments = this.post.comments.slice(
      0,
      this.commentDisplayCount
    );
    const hasMoreComments =
      this.post.comments.length > this.commentDisplayCount;

    return html`
      <div class="post">
        <div class="post-header">
          <div class="post-header-left">
            <img
              class="pfp-icon"
              src="${profileImage}"
              alt="User"
              @click=${() => gotoRoute(`/profile?user=${userId?._id}`)}
            />
            <div>
              <span
                class="post-author-link"
                @click=${() => gotoRoute(`/profile?user=${userId?._id}`)}
                >${authorName}</span
              >
              <p class="timestamp" title="${fullDate}">
                ${Utils.formatTime(createdAt)}
              </p>
            </div>
          </div>
          ${(userId?._id || userId) === Auth.currentUser?.id
            ? html`
                <sl-dropdown class="post-options" hoist placement="bottom-end">
                  <sl-icon-button
                    slot="trigger"
                    name="three-dots-vertical"
                    label="Post options"
                  ></sl-icon-button>
                  <sl-menu>
                    <sl-menu-item @click=${() => this.deletePost()}>
                      Delete Post
                    </sl-menu-item>
                  </sl-menu>
                </sl-dropdown>
              `
            : ''}
        </div>

        <div class="post-content">
          <p class="post-text">${text}</p>
          ${images.length
            ? html`
                <div class="post-img-group">
                  ${images.map(
                    (img, i) =>
                      html`<img
                        class="post-img"
                        src="${App.apiBase}/uploads/${img}"
                        @click=${() => this.openImageDialog(i)}
                      />`
                  )}
                </div>
              `
            : ''}
        </div>

        <div class="post-actions">
          <sl-icon-button
            name=${this.likedByUser ? 'hand-thumbs-up-fill' : 'hand-thumbs-up'}
            class=${this.likedByUser ? 'action-icon liked' : 'action-icon'}
            @click=${this.toggleLike}
          ></sl-icon-button>
          <span class="like-count">${this.post.likes?.length || 0}</span>
          <sl-icon-button
            name=${this.dislikedByUser
              ? 'hand-thumbs-down-fill'
              : 'hand-thumbs-down'}
            class=${this.dislikedByUser
              ? 'action-icon disliked'
              : 'action-icon'}
            @click=${this.toggleDislike}
          ></sl-icon-button>
          <span class="dislike-count">${this.post.dislikes?.length || 0}</span>
          <sl-icon
            name="chat-dots"
            class="action-icon comment-icon"
            @click=${() =>
              this.shadowRoot?.getElementById('commentInput')?.focus()}
          ></sl-icon>
          <span class="comment-count">${this.post.comments?.length || 0}</span>
        </div>

        <div
          class="post-comments ${this.post.comments.length === 0
            ? 'no-comments'
            : ''}"
        >
          ${visibleComments.map((comment) => {
            const user = comment.userId || {};
            const commenterName = Utils.getDisplayName(user);
            const commenterImage = Utils.getProfileImage(user);
            const commenterTimestamp = Utils.formatTime(comment.timestamp);

            return html`
              <div class="comment">
                <div class="comment-header">
                  <div class="comment-header-left">
                    <img
                      class="comment-pfp"
                      src="${commenterImage}"
                      alt="${commenterName}"
                    />
                    <div>
                      <strong
                        class="comment-author-link"
                        @click=${() => gotoRoute(`/profile?user=${user._id}`)}
                      >
                        ${commenterName}
                      </strong>
                      <p class="timestamp">${commenterTimestamp}</p>
                    </div>
                  </div>
                  ${user._id === Auth.currentUser?.id
                    ? html`
                        <sl-dropdown hoist placement="bottom-end">
                          <sl-icon-button
                            slot="trigger"
                            name="three-dots-vertical"
                            label="Comment options"
                            style="margin-left: auto"
                          ></sl-icon-button>
                          <sl-menu>
                            <sl-menu-item
                              @click=${() => this.deleteComment(comment._id)}
                            >
                              Delete Comment
                            </sl-menu-item>
                          </sl-menu>
                        </sl-dropdown>
                      `
                    : ''}
                </div>
                <div class="comment-text">
                  <p>${comment.text}</p>
                </div>
                <div class="comment-actions">
                  <sl-icon-button
                    name=${comment.likes.includes(Auth.currentUser?.id)
                      ? 'hand-thumbs-up-fill'
                      : 'hand-thumbs-up'}
                    class=${comment.likes.includes(Auth.currentUser?.id)
                      ? 'action-icon liked'
                      : 'action-icon'}
                    @click=${() =>
                      this.toggleCommentReaction(comment._id, 'like')}
                  ></sl-icon-button>
                  <span class="like-count">${comment.likes.length}</span>

                  <sl-icon-button
                    name=${comment.dislikes.includes(Auth.currentUser?.id)
                      ? 'hand-thumbs-down-fill'
                      : 'hand-thumbs-down'}
                    class=${comment.dislikes.includes(Auth.currentUser?.id)
                      ? 'action-icon disliked'
                      : 'action-icon'}
                    @click=${() =>
                      this.toggleCommentReaction(comment._id, 'dislike')}
                  ></sl-icon-button>
                  <span class="dislike-count">${comment.dislikes.length}</span>
                </div>
              </div>
            `;
          })}
          ${hasMoreComments
            ? html`
                <sl-button
                  class="view-more-comments"
                  size="small"
                  variant="text"
                  @click=${this.showMoreComments}
                >
                  View more comments
                </sl-button>
              `
            : ''}
        </div>

        <sl-dialog id="errorDialog" label="Error" class="error-dialog">
          <p id="errorMessage">Something went wrong.</p>
          <sl-button
            slot="footer"
            @click=${() => this.shadowRoot.getElementById('errorDialog').hide()}
          >
            OK
          </sl-button>
        </sl-dialog>

        <sl-dialog id="confirmDialog" label="Confirm" class="confirm-dialog">
          <p id="confirmMessage">Are you sure?</p>
          <sl-button
            slot="footer"
            variant="danger"
            @click=${() => {
              this.confirmationCallback?.();
              this.shadowRoot.getElementById('confirmDialog').hide();
            }}
            >Yes</sl-button
          >
          <sl-button
            slot="footer"
            variant="default"
            @click=${() =>
              this.shadowRoot.getElementById('confirmDialog').hide()}
            >Cancel</sl-button
          >
        </sl-dialog>

        <sl-dialog
          id="imageDialog"
          class="image-dialog"
          @sl-hide=${this.closeDialog}
          @click=${this.handleDialogClick}
          style="--width: 70%;"
        >
          <div class="dialog-layout">
            <div class="image-container">
              <sl-icon-button
                name="chevron-left"
                class="side-button left"
                ?disabled=${this.selectedImageIndex <= 0}
                @click=${() => this.navigateImage(-1)}
              ></sl-icon-button>
              <img
                src="${currentImage}"
                class="expanded-image ${this.slideDirection}"
                @animationend=${() => (this.slideDirection = 'none')}
              />
              <sl-icon-button
                name="chevron-right"
                class="side-button right"
                ?disabled=${this.selectedImageIndex >= images.length - 1}
                @click=${() => this.navigateImage(1)}
              ></sl-icon-button>
            </div>
          </div>
        </sl-dialog>

        <form class="comment-form" @submit=${this.submitComment}>
          <sl-input
            id="commentInput"
            placeholder="Write a comment..."
            filled
            .value=${this.commentText}
            @input=${this.handleCommentInput}
            required
          ></sl-input>
          <sl-button type="submit" variant="primary">Post</sl-button>
        </form>
      </div>
    `;
  }

  static styles = css`
    .post {
      background: var(--white);
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-bottom: 1px solid #eaeaea;
    }

    .pfp-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      object-fit: cover;
    }

    .post-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 55%;
    }

    .post-header .post-header-left {
      gap: 1.5rem;
      display: flex;
      align-items: center;
    }

    .post-options {
      margin-left: auto;
    }

    .post-author-link {
      font-weight: bold;
      color: var(--sl-color-primary-500);
      cursor: pointer;
    }

    .timestamp {
      font-size: 0.75rem;
      color: var(--sl-color-gray-500);
      margin: 0;
    }

    .post-content {
      padding-left: calc(64px + 1.5rem);
    }

    .post-img-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      margin: 1.5rem 0;
    }

    .post-img-group img {
      width: 200px;
      height: 150px;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.07);
      cursor: pointer;
      transition: transform 0.2s ease;
    }

    .post-img-group img:hover {
      transform: scale(1.02);
    }

    .post-actions {
      display: flex;
      align-items: center;
      padding-left: calc(64px + 1.5rem);
      margin-bottom: 1.5rem;
    }

    .post-actions span {
      display: flex;
      justify-content: center;
      width: 25px;
    }

    .comment-actions {
      display: flex;
      align-items: center;
      padding-left: 48px;
    }

    .comment-actions span {
      display: flex;
      justify-content: center;
      width: 25px;
    }

    .action-icon {
      font-size: 1.25rem;
      padding: var(--sl-spacing-x-small);
      cursor: pointer;
      color: var(--sl-color-primary-500);
    }

    .action-icon::part(base) {
      padding: 0px;
    }

    .comment-icon {
      color: var(--sl-color-gray-600);
    }

    .like-count,
    .dislike-count,
    .comment-count {
      font-size: 0.9rem;
      color: var(--sl-color-gray-500);
    }

    sl-icon-button.liked {
      color: green;
    }

    sl-icon-button.disliked {
      color: red;
    }

    .post-comments {
      padding-left: calc(64px + 1.5rem);
      display: flex;
      flex-direction: column;
      margin-bottom: 1.5rem;
      width: 50%;
    }

    .post-comments.no-comments {
      margin-bottom: 0;
    }

    .comment {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-top: solid var(--standard-border) 1px;
      border-bottom: solid var(--standard-border) 1px;
      padding: 0.5rem 0.75rem;
      font-size: 0.9rem;
    }

    .comment-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .comment-header .comment-header-left {
      gap: 1rem;
      display: flex;
      align-items: center;
    }

    .comment-pfp {
      width: 32px;
      height: 32px;
      object-fit: cover;
      border-radius: 50%;
    }

    .comment-text {
      padding-left: 48px;
      font-size: 0.9rem;
    }

    .comment-text p {
      margin: 10px 0px 5px 0px;
    }

    .comment-form {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      padding-left: calc(64px + 1.5rem);
      width: 50%;
    }

    .comment-form sl-input {
      flex-grow: 1;
    }

    .comment-author-link {
      font-weight: bold;
      color: var(--sl-color-primary-500);
      cursor: pointer;
    }

    .image-dialog::part(body) {
      padding: 0;
      width: 100%;
      max-width: none;
    }

    .image-dialog::part(panel) {
      height: 90vh;
      max-height: 90vh;
      background-color: transparent;
      box-shadow: none;
    }

    .image-dialog::part(header) {
      visibility: hidden;
      height: 0px;
    }

    .dialog-layout {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2rem;
      padding: 1rem;
      min-width: 700px;
      max-width: 90vw;
      height: 90%;
    }

    .image-container {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    .expanded-image {
      max-width: 80vw;
      max-height: 80vh;
      object-fit: contain;
      border-radius: 8px;
    }

    .expanded-image.left {
      animation: slideLeft 0.3s ease-in-out;
    }

    .expanded-image.right {
      animation: slideRight 0.3s ease-in-out;
    }

    @keyframes slideLeft {
      from {
        transform: translateX(-100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .side-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      background-color: var(--white);
      border-radius: 50%;
      font-size: 1.5rem;
      width: 40px;
      height: 40px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .side-button.left {
      left: 0.5rem;
    }

    .side-button.right {
      right: 0.5rem;
    }

    @media all and (max-width: 1024px) {
      .post-comments {
        width: 100%;
      }

      .image-dialog::part(panel) {
        height: 100vh;
        width: 100vw;
      }

      .expanded-image {
        width: 100%;
      }
    }

    @media all and (max-width: 768px) {
      .pfp-icon {
        width: 40px;
        height: 40px;
      }

      .dialog-layout {
        min-width: 0;
        gap: 1rem;
        padding: 0.5rem;
      }

      .post-header {
        gap: 1rem;
        width: 100%;
      }

      .post-content,
      .post-actions,
      .post-comments,
      .comment-form {
        padding-left: 0;
      }
    }
  `;
}

customElements.define('popin-post', PopinPost);
